import dayjs from 'dayjs'

import type { DateRangeValue } from 'src/components/Inputs/DateFilter/DateFilter'
import { ApplicantStage, HiringStage } from 'src/graphql/types/applicants'
import { formatSnakeValue } from 'src/lib/formatters'

/**
 * Everything the prospects strip knows how to compute. The container is only
 * Apollo wiring, so the query variables and the drill-through URLs are built
 * here where they can be unit-tested.
 */

/**
 * The four in-progress hiring stages, in the order the product shows them.
 *
 * Order matches `MAIN_STAGE_KEYS` in
 * `src/pages/SettingsPage/tabs/RulesTab/utils/pipelineBucketing.ts` — declared
 * again here rather than imported so this page doesn't reach into the settings
 * page, and so the values carry the `HiringStage` enum type instead of that
 * file's looser `StageKey` union.
 */
export const PROSPECT_LANES: HiringStage[] = [
  HiringStage.LEAD,
  HiringStage.APPLICATION,
  HiringStage.QUALIFICATION,
  HiringStage.ONBOARDING,
]

export interface OutcomeDefinition {
  stage: ApplicantStage
  /**
   * Each outcome records when it happened on its own column, and each is an
   * indexed `DateRangeFilter` on `ApplicantFiltersInput` and a `date-range`
   * filter key on the Applicants page — so the same string works for the query
   * and for the URL (`<key>Gte` / `<key>Lte`).
   */
  dateFilterKey: string
}

/**
 * Terminal outcomes, positive one first — the order `OUTCOME_STAGE_KEYS` uses.
 * These are deliberately not lanes: they are not people you still work.
 */
export const OUTCOMES: OutcomeDefinition[] = [
  { stage: ApplicantStage.HIRED, dateFilterKey: 'hiredDate' },
  { stage: ApplicantStage.NOT_INTERESTED, dateFilterKey: 'notInterestedDate' },
  { stage: ApplicantStage.DISQUALIFIED, dateFilterKey: 'disqualifiedDate' },
  { stage: ApplicantStage.TERMINATED, dateFilterKey: 'terminatedDate' },
]

/**
 * Labels come from the product's own formatter, never a hand-written string —
 * that is what makes `NOT_INTERESTED` render as "Not Interested" here and in
 * the Applicants filter at the same time.
 */
export const labelFor = (stage: string): string => formatSnakeValue(stage)

/** A row of `applicantMetrics.items`. `name` is the grouped column's value. */
export interface MetricItem {
  name: string | null
  value: number
}

export interface StageCount {
  stage: string
  label: string
  count: number
}

/**
 * Pulls one group's count out of an `applicantMetrics` result.
 *
 * Never read `applicantMetrics.totalCount` for this — the resolver returns
 * `rawMetrics.length`, i.e. the number of groups, so it reads 4 for the lanes
 * and 1 for an outcome.
 */
export const countFor = (
  items: MetricItem[] | undefined | null,
  name: string
): number => items?.find((item) => item.name === name)?.value ?? 0

/**
 * Zero-fills the four lanes in canonical order, so a stage nobody is sitting in
 * still gets a row rather than vanishing.
 *
 * Buckets outside the four are dropped, including the `null` one:
 * `hiringStage` is nullable, so grouping prospects by it can return
 * `{ name: null }`. Every prospect-creation path sets `LEAD`, so a null-stage
 * prospect is a data anomaly rather than a state — but it does mean this
 * widget's total can read lower than `?applicantStages=PROSPECT` on the
 * Applicants list. Deliberate, and it matches the old `ProspectStatistics`.
 */
export const buildLaneCounts = (
  items: MetricItem[] | undefined | null
): StageCount[] =>
  PROSPECT_LANES.map((stage) => ({
    stage,
    label: labelFor(stage),
    count: countFor(items, stage),
  }))

/** Total across the lanes — the sum, not `totalCount`. */
export const laneTotal = (lanes: StageCount[]): number =>
  lanes.reduce((sum, lane) => sum + lane.count, 0)

/**
 * "This month", built the same way as the shipped "This month" preset in
 * `src/components/Inputs/DateFilter/DateFilter.tsx`: start of the month through
 * *now*, not end of month. That matters — `hiredDate` is legitimately set in the
 * future for a start date, so an open-ended window would count people who have
 * not started yet.
 */
export const monthWindow = (now: Date): DateRangeValue => ({
  gte: dayjs(now).startOf('month').toDate(),
  lte: now,
})

const iso = (date: Date | null | undefined): string | undefined =>
  date ? date.toISOString() : undefined

const recruiterFilter = (recruiterIds: string[]) =>
  recruiterIds.length > 0 ? { recruiters: recruiterIds } : {}

/**
 * Variables for the lane counts: one grouped query over the four hiring stages.
 * An empty `recruiterIds` means no recruiter filter at all — company-wide.
 */
export const buildLaneVariables = (recruiterIds: string[]) => ({
  groupBy: 'hiringStage',
  filters: {
    applicantStages: {
      OR: [{ applicantStage: ApplicantStage.PROSPECT }],
    },
    ...recruiterFilter(recruiterIds),
  },
})

/**
 * Variables for one outcome count.
 *
 * Filters on the outcome's stage *and* its date column, so the number means
 * "currently HIRED and hired this month" — the same thing the drill-through URL
 * says. Summing buckets instead would be the "hired this month, wherever they
 * are now" reading, and the count would stop matching the list it opens.
 */
export const buildOutcomeVariables = (
  outcome: OutcomeDefinition,
  recruiterIds: string[],
  window: DateRangeValue
) => ({
  groupBy: 'applicantStage',
  filters: {
    applicantStages: { OR: [{ applicantStage: outcome.stage }] },
    [outcome.dateFilterKey]: { gte: iso(window.gte), lte: iso(window.lte) },
    ...recruiterFilter(recruiterIds),
  },
})

export type ProspectsFilterTarget =
  | { kind: 'lane'; hiringStage: HiringStage }
  | { kind: 'outcome'; outcome: OutcomeDefinition; window: DateRangeValue }

/**
 * Builds the Applicants URL a number drills through to.
 *
 * `loadFiltersFromUrl.ts` hydrates the sidebar generically from the query
 * string, so these are plain filter keys: `applicantStages` takes
 * `STAGE` or `STAGE-SUBOPTION`, and every date-range filter takes
 * `<key>Gte` / `<key>Lte`.
 *
 * There is a second emitter of Applicants URLs at
 * `src/pages/AnalyticsPage/AnalyticsPageV2/buildApplicantsFilterUrl.ts`. It is
 * not reused here because its `current-stage` mode forces the window onto
 * `applicationDate`, which cannot express "hired this month".
 */
export const buildProspectsFilterUrl = (
  target: ProspectsFilterTarget,
  recruiterIds: string[]
): string => {
  const params = new URLSearchParams()

  if (target.kind === 'lane') {
    params.set(
      'applicantStages',
      `${ApplicantStage.PROSPECT}-${target.hiringStage}`
    )
  } else {
    params.set('applicantStages', target.outcome.stage)
    const gte = iso(target.window.gte)
    const lte = iso(target.window.lte)
    if (gte) params.set(`${target.outcome.dateFilterKey}Gte`, gte)
    if (lte) params.set(`${target.outcome.dateFilterKey}Lte`, lte)
  }

  if (recruiterIds.length > 0) {
    params.set('recruiters', recruiterIds.join(','))
  }

  return `/applicants?${params.toString()}`
}
